import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { indexOf, all, pipe, toPairs, find } from 'ramda';
import { RequestBuilderPage, steps } from './utils';
import Step2 from '../Step2';
import Step3 from '../Step3';
import Step4 from '../Step4';
import Step5 from '../Step5';
import ProgressLine from '../../components/ProgressLine';
import RegularButton from '../../../../components/RegularButton';
import { IAppState } from '../../../../common/models/store/IAppState';
import { ISelectedPosition } from '../../../../common/models/position/ISelectedPosition';
import {
  changeReqCandidatesOwnerRoutine,
  createReqRefRoutine,
  setReqPositionRoutine,
  fetchRefReqEmailTemplatesRoutine,
  fetchLastRefCountersRoutine
} from '../../routines';
import { IBindingCallback1, IBindingAction } from '../../../../common/models/callback';
import { IUserInitials } from '../../../../common/models/user/IUserInitials';
import { User } from '../../../../common/models/user/User';
import { IRefReqCounters } from '../../../../common/models/referenceRequest/IRefReqCounters';
import { ISelectedSurvey } from '../../../../common/models/survey/ISelectedSurvey';
import { ICreateRefReq } from '../../../../common/models/referenceRequest/ICreateRefReq';
import { IRefReqSelectedContact } from '../../../../common/models/contact/IRefReqSelectedContact';
import LoaderWrapper from '../../../../components/LoaderWrapper';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import IconButton from '../../../../components/IconButton';
import { ReactComponent as ArrowLeft } from '../../../../assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRight } from '../../../../assets/icons/arrow-right.svg';
import { IReferenceReqPosition } from '../../../../common/models/position/IReferenceReqPosition';
import LinkButton from '../../../../components/LinkButton';
import { ReactComponent as PaperPlane } from '../../../../assets/icons/paper-plane.svg';
import styles from './styles.module.scss';
import { IEmailTemplate } from '../../../../common/models/email/IEmailTemplate';
import ChoosePositionPage from '../../../../containers/ChoosePositionPage';
import { INewContactInfo } from '../../../../common/models/contact/INewContactInfo';
import AddReferencesStep from '../AddReferencesStep';
import { simpleRequestCounters } from '../../../../common/helpers/refCountersHelper';

interface IProps {
  selectedPosition: ISelectedPosition | null;
  candidates: IRefReqSelectedContact[];
  refCounters: IRefReqCounters;
  user: User;
  selectedSurvey: ISelectedSurvey | null;
  sendSMS: boolean;
  emailTemplate?: IEmailTemplate;
  selectedOwner: IUserInitials;
  isSent: boolean;
  isSaving: boolean;
  disabledTypes: ReferenceType[];
  isAdvancedRequest: boolean;
  reviewReferencesEnabled: boolean;
  positions: IReferenceReqPosition[];
  references: INewContactInfo[] | null;
  changeOwner: IBindingCallback1<IUserInitials>;
  createRefReq: IBindingCallback1<ICreateRefReq>;
  onHideRequestBuilder: IBindingAction;
  fetchRefReqEmailTemplates: IBindingAction;
  setReqPosition: IBindingCallback1<ISelectedPosition | null>;
  getLastCounters: IBindingCallback1<string | null>;
}

export interface IReqBuilderHandle {
  confirmClose: IBindingAction;
}

const RequestBuilder = forwardRef<IReqBuilderHandle, IProps>(({
  reviewReferencesEnabled,
  isAdvancedRequest,
  selectedPosition,
  selectedSurvey,
  selectedOwner,
  disabledTypes,
  emailTemplate,
  refCounters,
  candidates,
  references,
  positions,
  isSaving,
  sendSMS,
  isSent,
  user: { firstName, lastName, id, initials },
  onHideRequestBuilder,
  fetchRefReqEmailTemplates,
  getLastCounters,
  setReqPosition,
  createRefReq,
  changeOwner
}, ref) => {
  const getDefaultPage = () => {
    if (Boolean(selectedPosition?.id) && candidates.length) {
      return RequestBuilderPage.Step2;
    }
    return RequestBuilderPage.Step1;
  };

  const [currentPage, setCurrentPage] = useState(getDefaultPage());
  const [isConfirmCloseShown, setIsConfirmCloseShown] = useState(false);

  const currentPageNumber = indexOf(currentPage, steps);
  const getCurrentPage = () => {
    switch (currentPage) {
      case RequestBuilderPage.Step1:
        return <ChoosePositionPage selectedPosition={selectedPosition} setPosition={setReqPosition} />;
      case RequestBuilderPage.Step2:
        return <Step2 />;
      case RequestBuilderPage.Step3:
        return references ? <AddReferencesStep /> : <Step3 />;
      case RequestBuilderPage.Step4:
        return <Step4 />;
      case RequestBuilderPage.Step5:
        return <Step5 goToStep={setCurrentPage} />;
      default:
        return null;
    }
  };

  const onNextStep = () => setCurrentPage(steps[currentPageNumber + 1]);

  const onPrevStep = () => setCurrentPage(steps[currentPageNumber - 1]);

  const onToggleStep1 = () => {
    if (selectedPosition && !selectedPosition.id) {
      const position = find(
        ({ name }) => name.trim().toLowerCase() === selectedPosition.name.trim().toLowerCase(),
        positions
      );
      setReqPosition({ id: position?.id, name: position?.name || selectedPosition.name.trim() });
    }
    onNextStep();
  };

  const onSkipPosition = () => {
    setReqPosition(null);
    onNextStep();
  };

  const isThirdStepAvailable = () => !pipe<Record<keyof IRefReqCounters, number>, [string, number][], boolean>(
    toPairs,
    all(([key, val]) => !(val + 1) || key === 'totalCount')
  )(refCounters);

  const isNextStepAvailable = () => {
    switch (currentPage) {
      case RequestBuilderPage.Step1:
        return Boolean(selectedPosition);
      case RequestBuilderPage.Step2:
        return Boolean(candidates.length);
      case RequestBuilderPage.Step3:
        return references ? Boolean(references.length) : (Boolean(refCounters.totalCount) && isThirdStepAvailable());
      case RequestBuilderPage.Step4:
        return Boolean(selectedSurvey);
      default:
        return true;
    }
  };

  const onSend = () => createRefReq({
    selectedPosition,
    selectedSurvey: selectedSurvey as ISelectedSurvey,
    candidates,
    refCounters: isAdvancedRequest ? refCounters : { ...simpleRequestCounters, totalCount: refCounters.totalCount },
    disabledTypes: isAdvancedRequest ? disabledTypes : [],
    selectedOwner,
    messageText: (emailTemplate as IEmailTemplate).body,
    sendSMS,
    reviewReferencesEnabled,
    references
  });

  useEffect(() => {
    changeOwner({ firstName, lastName, id, initials });
    fetchRefReqEmailTemplates();
  }, []);

  useEffect(() => {
    if (isSent) onHideRequestBuilder();
  }, [isSent]);

  useEffect(() => {
    getLastCounters(selectedPosition?.id || null);
  }, [selectedPosition?.id]);

  useImperativeHandle(ref, () => ({
    confirmClose() {
      setIsConfirmCloseShown(true);
    }
  }));

  return (
    <LoaderWrapper loading={isSaving} transparent>
      <ConfirmationModal
        isShown={isConfirmCloseShown}
        titleText={{
          id: 'requestBuilder.quit',
          defaultMessage: 'Quit reference request'
        }}
        descriptionText={{
          id: 'requestBuilder.confirmClose',
          defaultMessage: 'Are you sure you want to quit this reference request? All progress will be lost.'
        }}
        confirmBttnText={{ id: 'confirmDelete', defaultMessage: 'Yes, delete' }}
        cancelBttnText={{ id: 'keepBttn', defaultMessage: 'No, keep' }}
        onCancel={() => setIsConfirmCloseShown(false)}
        onConfirm={() => { setIsConfirmCloseShown(false); onHideRequestBuilder(); }}
      />
      <div className={styles.requestBuilder}>
        <header className={styles.requestBuilderHeader}>
          <div className={styles.reqBuilderTitle}>
            <FormattedMessage id="requestBuilder.newRequest" defaultMessage="New Reference Request" />
          </div>
          <ProgressLine activeNumber={currentPageNumber + 1} />
        </header>
        {getCurrentPage()}
        <footer className={styles.reqBuilderBttns}>
          {currentPage !== RequestBuilderPage.Step1 && (
            <IconButton
              onClick={onPrevStep}
              variant="bordered"
              icon={<ArrowLeft />}
            />
          )}
          <div className={styles.rightButtons}>
            {currentPage === RequestBuilderPage.Step1 && (
              <LinkButton
                onClick={onSkipPosition}
                textIntlId="skip"
                defaultMessage="Skip"
                disabled
                enableAction
              />
            )}
            {currentPage === RequestBuilderPage.Step5
              ? (
                <RegularButton
                  onClick={onSend}
                  textIntlId={references ? 'review.sendToRef' : 'review.sendToCand'}
                  defaultMessage={references ? 'Send to references' : 'Send to candidate'}
                  icon={<PaperPlane />}
                />
              )
              : (
                <RegularButton
                  disabled={!isNextStepAvailable()}
                  onClick={currentPage === RequestBuilderPage.Step1 ? onToggleStep1 : onNextStep}
                  textIntlId="nextStep"
                  defaultMessage="Next step"
                  icon={<ArrowRight />}
                  iconPosition="right"
                />
              )}
          </div>
        </footer>
      </div>
    </LoaderWrapper>
  );
});

const mapStateToProps = ({
  requestBuilder: {
    selectedPosition,
    candidates,
    refCounters,
    selectedSurvey,
    sendSMS,
    selectedOwner,
    emailTemplate,
    isSent,
    isSaving,
    disabledTypes,
    isAdvancedRequest,
    reviewReferencesEnabled,
    references
  },
  user: { user },
  choosePosition: { positions }
}: IAppState) => ({
  selectedPosition,
  candidates,
  user: user as User,
  refCounters,
  selectedSurvey,
  sendSMS,
  selectedOwner,
  emailTemplate,
  isSent,
  isSaving,
  disabledTypes,
  isAdvancedRequest,
  positions,
  reviewReferencesEnabled,
  references
});

const mapDispatchToProps = {
  changeOwner: changeReqCandidatesOwnerRoutine,
  createRefReq: createReqRefRoutine,
  setReqPosition: setReqPositionRoutine,
  fetchRefReqEmailTemplates: fetchRefReqEmailTemplatesRoutine,
  getLastCounters: fetchLastRefCountersRoutine
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(RequestBuilder);
